// ============================================================
// MyHomePage — 員工專屬首頁
// ============================================================
// 設計哲學：
//   - iPhone 開啟 App 後 3 秒內完成打卡
//   - 上方大資訊：今天班次 + 大打卡按鈕
//   - 中段：本週工時、特休餘額、可轉補休
//   - 下段：公告（橫向輪播）+ 注意事項（可展開）
//   - 最後：快速進入「我的班表」「請假」「密碼」
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { QrScannerModal } from './QrScannerModal';
import { useAuthStore } from '../stores/authStore';
import { formatTaipeiTime, taipeiToday, taipeiWeekStart } from '../lib/datetime';
import { getBestPosition } from '../lib/geolocation';

interface TodaySchedule {
  id: string;
  startTime: string;
  endTime: string;
  position?: string;
  template?: { name: string; color: string };
  breakStart?: string;
  breakEnd?: string;
  isPublished: boolean;
}

interface TodayAttendance {
  clockIn: string | null;
  clockOut: string | null;
  breakOut: string | null;
  breakIn: string | null;
  status: string;
}

interface Announcement {
  id: string;
  type: 'ANNOUNCEMENT' | 'NOTICE';
  title: string;
  body: string;
  publishedAt: string;
  pinned?: number;
}

interface CompBalance {
  year: number;
  totalOvertimeHours: number;
  totalConvertedHours: number;
  availableCompHours: number;
}

interface LeaveBalance {
  leaveType: string;
  entitled: number;
  used: number;
  remaining: number;
}

export function MyHomePage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [todaySchedule, setTodaySchedule] = useState<TodaySchedule | null>(null);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance | null>(null);
  // 後端 /attendance/today 回傳的權威狀態：
  // NOT_STARTED | WORKING | ON_BREAK | RETURNED | FINISHED
  const [todayState, setTodayState] = useState<string>('NOT_STARTED');
  // 本店啟用的打卡方式（由後端 /attendance/today 回傳）
  const [clockMethods, setClockMethods] = useState<string[]>([]);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [expandedNotice, setExpandedNotice] = useState<string | null>(null);
  const [compBalance, setCompBalance] = useState<CompBalance | null>(null);
  const [annualBalance, setAnnualBalance] = useState<LeaveBalance | null>(null);
  const [now, setNow] = useState(new Date());
  const [missingClockIns, setMissingClockIns] = useState<number>(0);
  const [showQrFallback, setShowQrFallback] = useState(false);
  const [pendingEndpoint, setPendingEndpoint] = useState<string | null>(null);
  const [clocking, setClocking] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  // GPS 失敗時顯示原因與備援選項（不再自動開相機——員工看不到原因，只覺得「又要掃」）
  const [gpsFallback, setGpsFallback] = useState<{ text: string; endpoint: string } | null>(null);

  // 即時時鐘
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    // 一律用台北日期。原本是 toISOString().slice(0,10)（UTC 日期），
    // 台北 08:00 前會抓成前一天：首頁顯示昨天的班表，
    // 且昨天已完成的打卡會讓大按鈕變成「今日已完成打卡」。
    const today = taipeiToday();

    const year = taipeiToday().slice(0, 4);

    // 六個請求「同時」發出，不要一個等一個。
    // 每個請求到資料庫的往返約 0.3–0.5 秒，串接起來要三秒以上；
    // 並行之後總時間約等於最慢的那一個。
    // 用 allSettled：任何一項失敗都不影響其他項目照常顯示。
    const [schRes, todayRes, annRes, compRes, leaveRes, missRes] = await Promise.allSettled([
      api.get<{ schedules: (TodaySchedule & { date: string; userId: string })[] }>(
        `/schedules?weekStart=${taipeiWeekStart()}`),
      api.get<{ attendance: TodayAttendance | null; state: string; clockMethods?: string[] }>('/attendance/today'),
      api.get<{ announcements: Announcement[] }>('/announcements'),
      api.get<CompBalance>(`/comp-leave/balance/${user.id}?year=${year}`),
      api.get<{ balances: LeaveBalance[] }>(`/leaves/balance/${user.id}?year=${year}`),
      api.get<{ issues: unknown[] }>('/attendance-correction/missing'),
    ]);

    if (schRes.status === 'fulfilled') {
      const mine = schRes.value.schedules.filter(s => s.userId === user.id);
      setTodaySchedule(mine.find(s => s.date.startsWith(today)) ?? null);

      // 本週工時（依排班時數估算）
      let total = 0;
      for (const s of mine) {
        const [sh, sm] = s.startTime.split(':').map(Number);
        const [eh, em] = s.endTime.split(':').map(Number);
        let mins = (eh * 60 + em) - (sh * 60 + sm);
        if (s.breakStart && s.breakEnd) {
          const [bsh, bsm] = s.breakStart.split(':').map(Number);
          const [beh, bem] = s.breakEnd.split(':').map(Number);
          mins -= (beh * 60 + bem) - (bsh * 60 + bsm);
        }
        total += Math.max(0, mins);
      }
      setWeeklyHours(Math.round(total / 60 * 10) / 10);
    }

    if (todayRes.status === 'fulfilled') {
      setTodayAttendance(todayRes.value.attendance);
      setTodayState(todayRes.value.state);
      if (todayRes.value.clockMethods?.length) setClockMethods(todayRes.value.clockMethods);
    }
    if (annRes.status === 'fulfilled') setAnnouncements(annRes.value.announcements);
    if (compRes.status === 'fulfilled') setCompBalance(compRes.value);
    if (leaveRes.status === 'fulfilled') {
      setAnnualBalance(leaveRes.value.balances.find(b => b.leaveType === 'ANNUAL') ?? null);
    }
    if (missRes.status === 'fulfilled') setMissingClockIns(missRes.value.issues.length);
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 真正送出打卡（payload 已備好）
  const submitClock = async (endpoint: string, body: Record<string, unknown>) => {
    try {
      await api.post(endpoint, body);
      setGpsFallback(null);
      setMsg({ type: 'ok', text: endpoint.includes('in') ? '✓ 上班打卡成功' : '✓ 下班打卡成功' });
      await fetchAll();
    } catch (err) {
      setMsg({ type: 'err', text: (err as Error).message });
    }
  };

  const handleClock = async () => {
    if (!primaryAction) {
      setMsg({ type: 'err', text: '今日已完成打卡' });
      return;
    }
    // 下班會結束當日工時且不可逆，送出前確認。
    // （文字刻意不提「休息」二字——先前寫「若只是要休息…」，
    //   員工按下下班後看到訊息裡出現「休息」，誤以為自己按錯成休息。）
    if (primaryAction.danger) {
      const ok = window.confirm(`確定要下班嗎？\n\n按「確定」就會結束今天的工作時間。`);
      if (!ok) return;
    }

    setClocking(true);
    setMsg(null);
    setGpsFallback(null);
    try {
      const endpoint = primaryAction.endpoint;

      // 休息的開始/結束不需驗證位置，直接送出
      if (!primaryAction.needsLocation) {
        try {
          await api.post(endpoint, {});
          setMsg({ type: 'ok', text: `✓ ${primaryAction.label}` });
          await fetchAll();
        } catch (err) {
          setMsg({ type: 'err', text: (err as Error).message });
        }
        return;
      }

      // ── 依「本店實際啟用的方式」決定怎麼驗證 ──
      // 先前的寫法是一律先試 GPS，失敗才跳掃描。但本店只啟用 QR 與 WIFI，
      // GPS 一定被後端拒絕（此店鋪未啟用GPS驗證），於是每次打卡都要開相機掃碼。
      // 現在改為：能一鍵完成的方式優先，掃描是最後手段。
      const methods = clockMethods.length ? clockMethods : ['QR'];

      // ① GPS：一鍵且「真的能驗證人在店裡」，因此排在最前面。
      //    需要定位權限，但不必開相機。手機回報的誤差一併送出，後端會算進去。
      let gpsFailText: string | null = null;
      if (methods.includes('GPS')) {
        try {
          const fix = await getBestPosition();
          try {
            await api.post(endpoint, { method: 'GPS', ...fix });
            setMsg({ type: 'ok', text: `✓ ${primaryAction.label}成功（GPS）` });
            await fetchAll();
            return;
          } catch (err) {
            const m = (err as Error).message;
            // 不是位置問題（例如已經打過卡、伺服器沒回應）→ 直接顯示
            if (!m.includes('GPS') && !m.includes('範圍') && !m.includes('公尺') && !m.includes('定位')) {
              setMsg({ type: 'err', text: m });
              return;
            }
            gpsFailText = m;
          }
        } catch (geoErr) {
          // 手機端就抓不到位置（權限被拒、逾時）→ 原因寫在訊息裡
          gpsFailText = (geoErr as Error).message;
        }
      }

      // ② WiFi：瀏覽器讀不到真實 SSID，等於一鍵放行、不具驗證力，
      //    所以排在 GPS 之後，只在沒有更可靠的方式時才用。
      if (methods.includes('WIFI')) {
        try {
          await api.post(endpoint, { method: 'WIFI' });
          setMsg({ type: 'ok', text: `✓ ${primaryAction.label}成功` });
          await fetchAll();
          return;
        } catch (err) {
          const m = (err as Error).message;
          // WiFi 不符 → 再試其他方式；其他錯誤直接顯示
          if (!m.includes('WiFi') && !m.includes('WIFI')) {
            setMsg({ type: 'err', text: m });
            return;
          }
        }
      }

      // ③ 最後才用 QR 掃描。
      //    GPS 試過且失敗 → 先讓員工看到原因，由他選擇掃 QR 或去櫃檯打卡站；
      //    本店根本沒啟用 GPS → 跟以前一樣直接開相機。
      if (methods.includes('QR')) {
        if (gpsFailText) {
          setGpsFallback({ text: gpsFailText, endpoint });
        } else {
          setPendingEndpoint(endpoint);
          setShowQrFallback(true);
        }
        return;
      }

      setMsg({ type: 'err', text: gpsFailText ?? '本店尚未設定可用的打卡方式，請聯絡主管' });
    } finally {
      setClocking(false);
    }
  };

  // 開始休息（不需要 GPS/QR 驗證，屬於同一次出勤內的狀態變更）
  const handleBreakOut = async () => {
    setClocking(true);
    setMsg(null);
    try {
      await api.post('/attendance/break-out', {});
      setMsg({ type: 'ok', text: '✓ 已開始休息' });
      await fetchAll();
    } catch (err) {
      setMsg({ type: 'err', text: (err as Error).message });
    } finally {
      setClocking(false);
    }
  };

  // QR 掃描成功 → 用該 token 送打卡
  const handleQrScanned = async (qrPayload: string) => {
    setShowQrFallback(false);
    if (!pendingEndpoint) return;

    let token = qrPayload;
    try {
      const parsed = JSON.parse(qrPayload);
      token = parsed.token ?? qrPayload;
    } catch { /* 非 JSON 直接當 token */ }

    setClocking(true);
    try {
      await submitClock(pendingEndpoint, { method: 'QR', qrToken: token });
    } finally {
      setClocking(false);
      setPendingEndpoint(null);
    }
  };

  const pinned = announcements.filter(a => a.type === 'NOTICE').sort((a, b) => (b.pinned ?? 0) - (a.pinned ?? 0));
  const news = announcements.filter(a => a.type === 'ANNOUNCEMENT');

  // 判斷現在的打卡狀態。
  // ⚠️ ON_BREAK / RETURNED 必須納入：先前這裡只認得「上班/下班」兩態，
  //    員工在休息中按下大按鈕，送出的是「下班打卡」——正是 5/8 事件裡
  //    ClockInPage 加了確認對話框要防止的那件事，但首頁沒有跟著防。
  const clockState:
    | 'NOT_YET' | 'UPCOMING' | 'WORKING' | 'ON_BREAK' | 'RETURNED' | 'DONE' | 'NO_SHIFT' = (() => {
    if (!todaySchedule) return 'NO_SHIFT';
    if (todayState === 'FINISHED') return 'DONE';
    if (todayState === 'ON_BREAK') return 'ON_BREAK';
    if (todayState === 'RETURNED') return 'RETURNED';
    if (todayState === 'WORKING') return 'WORKING';
    const [sh, sm] = todaySchedule.startTime.split(':').map(Number);
    const scheduledStart = new Date(now);
    scheduledStart.setHours(sh, sm, 0, 0);
    return now < scheduledStart ? 'UPCOMING' : 'NOT_YET';
  })();

  // 主要按鈕要做的動作
  // needsLocation：上下班需要 GPS/QR 驗證位置；休息的開始與結束屬於
  // 同一次出勤內的狀態變更，後端不驗位置，前端也不該跳出掃碼流程。
  const primaryAction:
    | { endpoint: string; label: string; danger: boolean; needsLocation: boolean }
    | null = (() => {
    switch (clockState) {
      case 'ON_BREAK':
        return { endpoint: '/attendance/break-in', label: '結束休息', danger: false, needsLocation: false };
      case 'WORKING':
      case 'RETURNED':
        return { endpoint: '/attendance/clock-out', label: '下班打卡', danger: true, needsLocation: true };
      case 'DONE':
      case 'NO_SHIFT':
        return null;
      default:
        return { endpoint: '/attendance/clock-in', label: '上班打卡', danger: false, needsLocation: true };
    }
  })();

  const greeting = (() => {
    const h = now.getHours();
    if (h < 6) return '早安';
    if (h < 11) return '早安';
    if (h < 14) return '午安';
    if (h < 18) return '下午好';
    return '晚安';
  })();

  return (
    <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
      {/* 問候 + 時鐘 */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xl font-bold text-morandi-800">{greeting}，{user?.name}</div>
          <div className="text-xs text-morandi-400 mt-0.5">
            {now.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </div>
        </div>
        <div className="text-2xl font-mono font-bold text-morandi-700">
          {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
        </div>
      </div>

      {/* 今日班次 + 大打卡按鈕 */}
      <div className="card-morandi p-5 space-y-4">
        {clockState === 'NO_SHIFT' ? (
          <div className="text-center py-6">
            <div className="text-lg font-medium text-morandi-600">今天休息</div>
            <div className="text-sm text-morandi-400 mt-1">好好放鬆，明天再見</div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-morandi-400 mb-1">今天的班</div>
                <div className="text-lg font-bold text-morandi-800">
                  {todaySchedule?.template?.name ?? '自訂班'}
                </div>
                <div className="text-sm font-mono text-morandi-600">
                  {todaySchedule?.startTime} – {todaySchedule?.endTime}
                  {todaySchedule?.position && <span className="ml-2 text-morandi-400">@ {todaySchedule.position}</span>}
                </div>
              </div>
              <div
                className="w-3 h-12 rounded-full"
                style={{ backgroundColor: todaySchedule?.template?.color ?? '#8B9DAF' }}
              />
            </div>

            {/* 打卡狀態顯示 */}
            <div className="grid grid-cols-2 gap-2 py-2 border-y border-morandi-100">
              <div className="text-center">
                <div className="text-xs text-morandi-400">上班打卡</div>
                <div className="text-lg font-mono font-bold text-morandi-700">
                  {formatTaipeiTime(todayAttendance?.clockIn ?? null)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-morandi-400">下班打卡</div>
                <div className="text-lg font-mono font-bold text-morandi-700">
                  {formatTaipeiTime(todayAttendance?.clockOut ?? null)}
                </div>
              </div>
            </div>

            {/* 大打卡按鈕 */}
            {primaryAction ? (
              <>
                <button
                  onClick={handleClock}
                  disabled={clocking}
                  className={`w-full py-5 rounded-xl font-bold text-lg text-white transition-all shadow-lg
                    ${clocking
                      ? 'bg-morandi-300'
                      : primaryAction.danger
                        ? 'bg-rose-500 hover:bg-rose-600 active:scale-[0.98]'
                        : 'bg-sage-500 hover:bg-sage-600 active:scale-[0.98]'}`}
                >
                  {clocking ? '打卡中...' : (
                    clockState === 'UPCOMING'
                      ? `上班打卡（${todaySchedule?.startTime} 開始）`
                      : primaryAction.label
                  )}
                </button>

                {/* 上班中才顯示「開始休息」，與下班分開，避免按錯就結束一整天 */}
                {clockState === 'WORKING' && (
                  <button
                    onClick={handleBreakOut}
                    disabled={clocking}
                    className="w-full py-3 rounded-xl font-medium text-earth-700 bg-earth-100 hover:bg-earth-200 disabled:opacity-50 transition-all"
                  >
                    開始休息
                  </button>
                )}
              </>
            ) : (
              <div className="w-full py-4 rounded-xl bg-morandi-100 text-center text-morandi-500 text-sm">
                ✓ 今日已完成打卡，辛苦了
              </div>
            )}

            {msg && (
              <div className={`text-sm text-center px-3 py-2 rounded-lg
                ${msg.type === 'ok' ? 'bg-sage-100 text-sage-700' : 'bg-rose-100 text-rose-600'}`}>
                {msg.text}
              </div>
            )}

            {/* GPS 沒過：說清楚原因，再給備援 */}
            {gpsFallback && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 space-y-3">
                <div className="text-sm text-rose-700 leading-relaxed">{gpsFallback.text}</div>
                <button
                  onClick={() => { setPendingEndpoint(gpsFallback.endpoint); setShowQrFallback(true); }}
                  className="w-full py-3 rounded-xl font-medium text-white bg-smoke-500 hover:bg-smoke-600 active:scale-[0.98] transition-all"
                >
                  掃描店內 QR 打卡
                </button>
                <div className="text-xs text-center text-morandi-500">或直接到櫃檯打卡站，點名字輸入 PIN</div>
              </div>
            )}

            <button
              onClick={() => navigate('/clock')}
              className="w-full text-xs text-morandi-400 hover:text-morandi-600 py-1"
            >
              打卡遇到問題？改用其他方式 →
            </button>
          </>
        )}
      </div>

      {/* 統計 */}
      <div className="grid grid-cols-3 gap-2">
        <StatBox label="本週工時" value={`${weeklyHours}`} unit="hr" color="text-earth-600" />
        <StatBox
          label="特休剩餘"
          value={`${annualBalance?.remaining ?? '-'}`}
          unit="天"
          color="text-sage-600"
        />
        <button onClick={() => navigate('/comp-leave')} className="contents">
          <StatBox
            label="可轉補休"
            value={`${compBalance?.availableCompHours ?? 0}`}
            unit="hr"
            color="text-smoke-600"
          />
        </button>
      </div>

      {/* 漏打卡提醒 */}
      {missingClockIns > 0 && (
        <button
          onClick={() => navigate('/attendance-correction')}
          className="w-full card-morandi p-4 flex items-center gap-3 hover:bg-morandi-50 transition-colors text-left ring-1 ring-rose-200 bg-rose-50/30"
        >
          <span className="text-2xl">⚠</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-rose-700">
              你有 {missingClockIns} 筆漏打卡需補登
            </div>
            <div className="text-xs text-morandi-500 mt-0.5">點此查看並送出補登申請</div>
          </div>
          <span className="text-morandi-300">›</span>
        </button>
      )}

      {/* 最新公告 */}
      {news.length > 0 && (
        <div className="card-morandi p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-morandi-700">📢 最新公告</h2>
            <span className="badge bg-rose-100 text-rose-500 text-xs">{news.length}</span>
          </div>
          {news.slice(0, 3).map(a => (
            <div key={a.id} className="border-l-3 border-rose-300 pl-3 py-1">
              <div className="text-sm font-medium text-morandi-700">{a.title}</div>
              <div className="text-xs text-morandi-500 mt-0.5 whitespace-pre-wrap">{a.body}</div>
              <div className="text-xs text-morandi-300 mt-1">
                {new Date(a.publishedAt).toLocaleDateString('zh-TW')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 注意事項（可展開） */}
      {pinned.length > 0 && (
        <div className="card-morandi p-4 space-y-2">
          <h2 className="font-semibold text-morandi-700">📌 注意事項</h2>
          {pinned.map(a => (
            <button
              key={a.id}
              onClick={() => setExpandedNotice(expandedNotice === a.id ? null : a.id)}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between py-2 border-b border-morandi-100 last:border-b-0">
                <span className="text-sm text-morandi-700">{a.title}</span>
                <span className="text-morandi-300 text-xs">
                  {expandedNotice === a.id ? '▲' : '▼'}
                </span>
              </div>
              {expandedNotice === a.id && (
                <div className="text-sm text-morandi-500 whitespace-pre-wrap py-2 px-2 bg-morandi-50 rounded-lg">
                  {a.body}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {showQrFallback && (
        <QrScannerModal
          onScan={handleQrScanned}
          onClose={() => { setShowQrFallback(false); setPendingEndpoint(null); }}
        />
      )}

      {/* 快速功能 */}
      <div className="grid grid-cols-4 gap-2">
        <QuickBtn icon="📅" label="我的班表" onClick={() => navigate('/my-schedule')} />
        <QuickBtn icon="💰" label="薪資條" onClick={() => navigate('/my-payroll')} />
        <QuickBtn icon="✉" label="請假" onClick={() => navigate('/leaves')} />
        <QuickBtn icon="🔒" label="密碼" onClick={() => navigate('/password')} />
      </div>
    </div>
  );
}

function StatBox({ label, value, unit, color }: {
  label: string; value: string | number; unit: string; color: string;
}) {
  return (
    <div className="card-morandi p-3 text-center">
      <div className={`text-lg font-bold font-mono ${color}`}>
        {value}<span className="text-xs font-normal text-morandi-400 ml-0.5">{unit}</span>
      </div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}</div>
    </div>
  );
}

function QuickBtn({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="card-morandi p-3 flex flex-col items-center gap-1 hover:bg-morandi-50 transition-colors"
    >
      <span className="text-xl">{icon}</span>
      <span className="text-xs text-morandi-600">{label}</span>
    </button>
  );
}
